import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/CTA";
import { CardDivider } from "../../../components/Divider";
import { Dropdown } from "../../../components/Dropdown";
import { Flex } from "../../../components/Flex";
import { H3 } from "../../../components/Heading";
import { TrashIcon } from "../../../components/icons/TrashIcon";
import { Input } from "../../../components/Input";
import Loading from "../../../components/Loading";
import { Text } from "../../../components/Text";
import { View } from "../../../components/View";
import { MOBILE_BREAKPOINT } from "../../../config";
import {
  AccountType,
  useCreateBrandGroupInviteMutation,
  useDeleteBrandGroupAccountMutation,
  useDeleteBrandGroupInviteMutation,
  useGetBrandGroupQuery,
  useUpdateAcccountMutation,
} from "../../../graphql/generated";
import { queryKeys } from "../../../graphql/queryKeys";
import useAnalytics from "../../../hooks/useAnalytics";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import { styled } from "../../../styles";

export interface MatchParams {
  page: string;
}

export const GroupTeam = () => {
  const activeGroupId = useSelector(authSelectors.activeGroupId);
  const client = useGqlClient();
  const { track } = useAnalytics();

  const [email, setEmail] = useState("");
  const {
    mutate: createBrandGroupInvite,
    isLoading: isCreatingBrandGroupInvite,
  } = useCreateBrandGroupInviteMutation(client);
  const {
    mutate: deleteBrandGroupInvite,
    isLoading: isDeletingBrandGroupInvite,
  } = useDeleteBrandGroupInviteMutation(client);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching } = useGetBrandGroupQuery(client, {
    id: activeGroupId ? activeGroupId : "",
  });

  if (isLoading || isFetching || !data || isDeletingBrandGroupInvite) {
    return <Loading />;
  }

  if (!data.brandGroup) {
    return <Text>Something went wrong</Text>;
  }

  return (
    <StyledCard margin="l 0 xl 0">
      <View margin="0 0 m">
        <H3 margin="0 0 0">Manage Your Team</H3>
        <Text margin="0 0 0" colorPreset="secondary">
          Group team members will have access to all brands in the group,
          including future brands that are added
        </Text>
      </View>
      <InputWrap align="center" justify="space-between">
        <View style={{ flex: 1 }}>
          <Input
            inputSize="s"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value);
            }}
            placeholder="Enter email address"
          />
        </View>
        <View>
          <Button
            size="s"
            onClick={() => {
              if (isCreatingBrandGroupInvite || !data.brandGroup) {
                return;
              }

              createBrandGroupInvite(
                {
                  email,
                  brandGroupId: activeGroupId ?? "",
                },
                {
                  onSuccess: () => {
                    toast.success("Team member invited");
                    track("Team member invited");
                    setEmail("");
                    queryClient.resetQueries([queryKeys.getBrandGroup], {
                      exact: false,
                    });
                  },
                  onError: (error: any) => {
                    Sentry.captureException(error);
                  },
                }
              );
            }}
            isDisabled={email.trim().length > 0 ? false : true}
          >
            Invite User
          </Button>
        </View>
      </InputWrap>
      {data.brandGroup.accounts.length > 0 ||
      data.brandGroup.invites.length > 0 ? (
        <CardDivider margin="l 0 l 0" />
      ) : null}
      {data.brandGroup.accounts
        .sort((a, b) => {
          return a.firstName.localeCompare(b.firstName);
        })
        .map((account) => {
          return (
            <AccountItem
              accountId={account.id}
              accountEmail={account.email}
              accountName={`${account.firstName} ${account.lastName}`}
              onRefresh={() =>
                queryClient.resetQueries([queryKeys.getBrandGroup], {
                  exact: false,
                })
              }
              accountType={account.type}
            />
          );
        })}
      {data.brandGroup.invites.map((invite) => {
        return (
          <Row
            margin="0 0 0 0"
            justify="space-between"
            align="center"
            style={{ overflow: "hidden" }}
          >
            <div>
              <Text weight="bold" margin="0">
                {invite.email}
              </Text>
            </div>
            <Flex align="center" style={{ position: "relative" }}>
              <PendingText
                className="pending-text"
                size="s"
                margin="0"
                colorPreset="secondary"
              >
                Invite pending
              </PendingText>
              <TrashWrap
                className="trash-icon"
                onClick={() =>
                  deleteBrandGroupInvite(
                    {
                      brandGroupInviteId: invite.id,
                    },
                    {
                      onError: (e: any) => {
                        Sentry.captureException(e);
                      },
                      onSuccess: (res: any) => {
                        toast.success("Invite deleted");
                        track("Delete invite", {
                          id: res.deleteBrandInvite?.id
                            ? res.deleteBrandInvite.id
                            : undefined,
                          brandGroup: data?.brandGroup?.name,
                        });
                        queryClient.resetQueries([queryKeys.getBrandGroup], {
                          exact: false,
                        });
                      },
                    }
                  )
                }
              >
                <TrashIcon height={16} width={16} colorPreset="secondary" />
              </TrashWrap>
            </Flex>
          </Row>
        );
      })}
    </StyledCard>
  );
};

const Row = styled(Flex)`
  justify-content: space-between;
  position: relative;
  padding: ${(p) => p.theme.spacing.s} 0;

  &:hover {
    .trash-icon {
      transform: translateX(0);
      opacity: 1;
    }
    .pending-text {
      transform: translateX(-32px); // Adjust this value as needed
    }
  }
`;

interface AccountItemProps {
  accountId: string;
  accountName: string;
  accountEmail: string;
  accountType: AccountType;
  onRefresh: () => void;
}

function AccountItem(props: AccountItemProps) {
  const personalAccount = useSelector(authSelectors.account);

  const client = useGqlClient();
  const updateAccount = useUpdateAcccountMutation(client);
  const deleteBrandGroupAccount = useDeleteBrandGroupAccountMutation(client);
  const { track } = useAnalytics();

  return (
    <Row margin="0 0 0 0" justify="space-between" align="center">
      <div>
        <Text weight="bold" margin="0">
          {`${props.accountName}`}{" "}
          {props.accountType === AccountType.AccountTypeAdmin ? (
            <Text
              weight="semi"
              colorPreset="secondary"
              size="s"
              margin="0"
              isCompact
              isInline
            >
              {" "}
              Admin
            </Text>
          ) : null}
        </Text>
        <Text margin="0" isCompact colorPreset="secondary">
          {props.accountEmail}
        </Text>
      </div>
      {personalAccount && props.accountId !== personalAccount.id ? (
        <Dropdown
          dropdownType="actions"
          selectionMode="single"
          renderLabel={() => {
            return "Actions";
          }}
          anchor="top end"
          selectedOptions={[]}
          setSelectedOptions={(selectedOptions) => {
            const selectedValue = selectedOptions[0];

            if (selectedValue === "admin") {
              updateAccount.mutate(
                {
                  id: props.accountId,
                  type: AccountType.AccountTypeAdmin,
                },
                {
                  onSuccess: () => {
                    toast.success("Team member updated");
                    props.onRefresh();
                  },
                }
              );
              return;
            }

            if (selectedValue === "remove") {
              deleteBrandGroupAccount.mutate(
                {
                  accountId: props.accountId,
                },
                {
                  onSuccess: () => {
                    toast.success("Team member removed");
                    track("Team member removed", {
                      accountId: props.accountId,
                      accountEmail: props.accountEmail,
                    });
                    props.onRefresh();
                  },
                }
              );
            }
          }}
          options={[
            {
              label: "Make Admin",
              value: "admin",
            },
            {
              label: "Remove",
              value: "remove",
            },
          ]}
        />
      ) : null}
    </Row>
  );
}

const StyledCard = styled(Card)`
  padding: ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.l}
    ${(p) => p.theme.spacing.l};
  overflow: visible;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(p) => p.theme.spacing.l};
  }
`;

const TrashWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: ${(p) => p.theme.spacing.m};
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  transform: translateX(100%);
  opacity: 0;
  position: absolute;
  right: 0;
  top: 2px;
  width: 32px;
  transform: translateX(100%);
  align-items: center;
  justify-content: center;

  &:hover {
    svg {
      filter: brightness(0.25);
      cursor: pointer;
    }
  }
`;

const PendingText = styled(Text)`
  transition: transform 0.3s ease-in-out;
`;

const InputWrap = styled(Flex)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.m};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: ${(p) => p.theme.spacing.s};
    width: 100%;

    > div {
      width: 100%;
    }

    input {
      text-align: center;
      width: 100%;
    }

    button {
      width: 100%;
    }
  }
`;
